import { useQuery, useQueryClient } from '@tanstack/react-query'
import { useEffect } from 'react'
import { api } from './api'
import type { BusEvent, Catalog, CycleData, HomeData, Me, Versions, WorkspaceState } from './types'

export const keys = {
  me: ['me'] as const,
  ws: (wid: string) => ['ws', wid] as const,
  state: (wid: string) => ['ws', wid, 'state'] as const,
  home: (wid: string) => ['ws', wid, 'home'] as const,
  catalog: (wid: string, branch = 'main') => ['ws', wid, 'catalog', branch] as const,
  changesets: (wid: string, filter: string) => ['ws', wid, 'changesets', filter] as const,
  changeset: (wid: string, id: string) => ['ws', wid, 'changeset', id] as const,
  cycle: (wid: string, key?: string) => ['ws', wid, 'cycle', key ?? ''] as const,
  versions: (wid: string) => ['ws', wid, 'versions'] as const,
}

export function useMe() {
  return useQuery({ queryKey: keys.me, queryFn: () => api.get<Me>('/auth/me'), retry: false, staleTime: 30_000 })
}

export function useWorkspaceState(wid: string) {
  return useQuery({ queryKey: keys.state(wid), queryFn: () => api.get<WorkspaceState>(`/w/${wid}/state`) })
}

export function useHome(wid: string) {
  return useQuery({ queryKey: keys.home(wid), queryFn: () => api.get<HomeData>(`/w/${wid}/home`) })
}

export function useCatalog(wid: string, branch = 'main') {
  return useQuery({
    queryKey: keys.catalog(wid, branch),
    queryFn: () => api.get<Catalog>(`/w/${wid}/catalog?branch=${encodeURIComponent(branch)}`),
    staleTime: 60_000,
  })
}

export function useCycle(wid: string, key?: string) {
  return useQuery({
    queryKey: keys.cycle(wid, key),
    queryFn: () => api.get<CycleData>(`/w/${wid}/cycle${key ? `?cycle=${encodeURIComponent(key)}` : ''}`),
  })
}

/** Every version a view can show: the working forecast, actuals, the latest estimate, snapshots and open scenarios. */
export function useVersions(wid: string) {
  return useQuery({ queryKey: keys.versions(wid), queryFn: () => api.get<Versions>(`/w/${wid}/versions`) })
}

/** Live workspace events: refresh what they touch, and let the caller react (toasts). */
export function useWorkspaceEvents(wid: string | null, onEvent?: (e: BusEvent) => void) {
  const qc = useQueryClient()
  useEffect(() => {
    if (!wid) return
    let es: EventSource | null = null
    let retry: ReturnType<typeof setTimeout> | undefined
    let closed = false
    const connect = () => {
      es = new EventSource(`/api/w/${wid}/events`)
      es.onmessage = m => {
        let e: BusEvent
        try {
          e = JSON.parse(m.data) as BusEvent
        } catch {
          return
        }
        if (e.type === 'changeset' || e.type === 'merged') {
          void qc.invalidateQueries({ queryKey: keys.ws(wid) })
        } else if (e.type === 'tool') {
          void qc.invalidateQueries({ queryKey: ['ws', wid, 'changesets'] })
        }
        onEvent?.(e)
      }
      es.onerror = () => {
        es?.close()
        if (!closed) retry = setTimeout(connect, 3000)
      }
    }
    connect()
    return () => {
      closed = true
      clearTimeout(retry)
      es?.close()
    }
  }, [wid, qc, onEvent])
}
